import { extractCollection, extractPayload } from '@/utils/dataNormalization';
import { normalizeCatalogOptions } from '@/utils/catalogOptions';
import { SALES_TEXT } from '@/texts/catalog/sales';
import { TAX_MODE_OPTIONS } from '@/composables/shared/useTaxModeOptions';
import { PAYMENT_PLAN_DOCUMENT_TYPE_OPTIONS } from '@/composables/paymentPlans/usePaymentPlanModalForm';

const emptyOption = { value: '', label: SALES_TEXT.modal.placeholders.emptyOption };
const silentRequestConfig = {};

const normalizePaymentPeriodOptions = (items) => {
  return (Array.isArray(items) ? items : [])
    .map((item) => {
      const code = String(item?.code ?? '').trim().toLowerCase();
      const name = String(item?.name ?? '').trim();

      if (code === '' || name === '') {
        return null;
      }

      return {
        value: code,
        label: name,
        meta: {
          ...item,
          code,
          name,
        },
      };
    })
    .filter(Boolean);
};

const isFlatRateService = (service) => {
  const typeCode = String(service?.service_type_code || '').trim().toUpperCase();
  if (typeCode === 'IGL') {
    return true;
  }

  const typeName = String(service?.service_type_name || '').trim().toLowerCase();
  return typeName === 'iguala' || typeName === 'retainer';
};

export const fetchSaleModalCatalogs = async ({
  servicesService,
  salesService,
  areasService,
  collaboratorsService,
  tagsService,
  quotesService,
  taxesService = null,
  generalCatalogsService = null,
  customerId = 0,
  isEditMode = false,
  serviceFilterMode = 'standard',
  includePaymentPlanCatalogs = false,
}) => {
  const normalizedCustomerId = Number(customerId || 0);
  const quoteSelectorsPromise = normalizedCustomerId > 0
    ? quotesService.selectors({ company_id: normalizedCustomerId }, silentRequestConfig)
    : Promise.resolve({ data: { data: [] } });

  const statusParams = isEditMode ? {} : { is_open: 1 };

  const requests = [
    servicesService.index({}, silentRequestConfig),
    salesService.statuses(statusParams, silentRequestConfig),
    areasService.index(silentRequestConfig),
    collaboratorsService.sellers({}, silentRequestConfig),
    collaboratorsService.managers({}, silentRequestConfig),
    tagsService.index({}, silentRequestConfig),
    quoteSelectorsPromise,
    quotesService.quoteTypeSelectors({}, silentRequestConfig),
    salesService.saleCode({}, silentRequestConfig),
  ];

  const shouldLoadPaymentPlanCatalogs = includePaymentPlanCatalogs && taxesService && generalCatalogsService;

  if (shouldLoadPaymentPlanCatalogs) {
    requests.push(taxesService.selectors({}, silentRequestConfig));
    requests.push(taxesService.groupSelectors({}, silentRequestConfig));
    requests.push(generalCatalogsService.getPaymentPeriods(silentRequestConfig));
  }

  const results = await Promise.allSettled(requests);

  const [
    servicesRes,
    statusesRes,
    areasRes,
    sellersRes,
    managersRes,
    tagsRes,
    quotesRes,
    quoteTypesRes,
    saleCodeRes,
    taxesRes,
    taxGroupsRes,
    periodicitiesRes,
  ] = results;

  const servicesRaw = servicesRes.status === 'fulfilled' ? extractCollection(servicesRes.value) : [];
  const blockedServiceTypeNames = new Set(['iguala', 'multiple cliente']);
  const normalizedServiceFilterMode = String(serviceFilterMode || '').trim().toLowerCase();

  const filteredServices = servicesRaw.filter((service) => {
    if (normalizedServiceFilterMode === 'flat-rate') {
      return isFlatRateService(service);
    }

    const typeCode = String(service?.service_type_code || '').trim().toUpperCase();
    if (typeCode === 'IGL' || typeCode === 'MULT') {
      return false;
    }

    const typeName = String(service?.service_type_name || '').trim().toLowerCase();
    if (blockedServiceTypeNames.has(typeName)) {
      return false;
    }

    return true;
  });

  const statusesRaw = statusesRes.status === 'fulfilled' ? extractCollection(statusesRes.value) : [];

  const tagsRaw = tagsRes.status === 'fulfilled' ? extractCollection(tagsRes.value) : [];
  const quotesRaw = quotesRes.status === 'fulfilled' ? extractCollection(quotesRes.value) : [];
  const quoteTypesRaw = quoteTypesRes.status === 'fulfilled' ? extractCollection(quoteTypesRes.value) : [];
  const initialSaleCode = saleCodeRes.status === 'fulfilled' ? extractPayload(saleCodeRes.value) : {};
  const taxesRaw = taxesRes?.status === 'fulfilled' ? extractCollection(taxesRes.value) : [];
  const taxGroupsRaw = taxGroupsRes?.status === 'fulfilled' ? extractCollection(taxGroupsRes.value) : [];
  const periodicitiesRaw = periodicitiesRes?.status === 'fulfilled' ? extractCollection(periodicitiesRes.value) : [];

  const nextConsecutiveType = String(initialSaleCode?.consecutive_type || '').trim().toLowerCase();
  const consecutiveType = nextConsecutiveType === 'services' ? 'services' : 'general';
  const initialCode = consecutiveType === 'general' ? String(initialSaleCode?.code || '').trim() : '';

  const normalizedStatuses = normalizeCatalogOptions(statusesRaw, { useCodeInLabel: false });

  return {
    options: {
      services: normalizeCatalogOptions(filteredServices, { useCodeInLabel: false }),
      statuses: [emptyOption, ...normalizedStatuses],
      areas: [emptyOption, ...normalizeCatalogOptions(areasRes.status === 'fulfilled' ? extractCollection(areasRes.value) : [])],
      sellers: [emptyOption, ...normalizeCatalogOptions(sellersRes.status === 'fulfilled' ? extractCollection(sellersRes.value) : [])],
      managers: [emptyOption, ...normalizeCatalogOptions(managersRes.status === 'fulfilled' ? extractCollection(managersRes.value) : [])],
      quoteTypes: [emptyOption, ...normalizeCatalogOptions(quoteTypesRaw, { useCodeInLabel: false })],
      quoteCatalogs: normalizeCatalogOptions(quotesRaw, { useCodeInLabel: false }),
      documentTypes: PAYMENT_PLAN_DOCUMENT_TYPE_OPTIONS,
      taxModes: TAX_MODE_OPTIONS,
      taxes: normalizeCatalogOptions(taxesRaw, { useCodeInLabel: false }),
      taxGroups: normalizeCatalogOptions(taxGroupsRaw, { useCodeInLabel: false }),
      periodicities: normalizePaymentPeriodOptions(periodicitiesRaw),
    },
    tagOptions: tagsRaw
      .map((tag) => String(tag?.name || '').trim())
      .filter(Boolean),
    consecutiveType,
    initialCode,
  };
};
